import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ArrowUpDown, Calendar, ChevronDown, Check, CreditCard, DollarSign, Search, Users, Plus, Settings } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/auth-provider";
import { AdminLayout } from "@/components/admin-layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import type { AdminPlan, AdminSubscription, AdminUser } from "@/types/admin";

// Form schema for creating new customer
const createCustomerSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
  businessName: z.string().min(2, "Business name must be at least 2 characters"),
  industry: z.string().min(1, "Please select an industry"),
  planId: z.string().min(1, "Please select a subscription plan"),
});

type CreateCustomerFormData = z.infer<typeof createCustomerSchema>;

export default function ClientManagementPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<any>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [newPlanId, setNewPlanId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState("users");
  
  // Check if user is admin (demo user gets admin access in development)
  const isAdmin = Boolean(user && (user.isAdmin || user.email === "hello@zentrobooks.com"));
  
  // Fetch all users with their businesses and subscriptions
  const { 
    data: users = [], 
    isLoading: isLoadingUsers,
  } = useQuery<AdminUser[]>({
    queryKey: ['/api/admin/users'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/admin/users');
      return res.json();
    },
    enabled: isAdmin
  });
  
  // Fetch all business subscriptions
  const {
    data: subscriptions = [],
    isLoading: isLoadingSubscriptions,
  } = useQuery<AdminSubscription[]>({
    queryKey: ['/api/admin/subscriptions'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/admin/subscriptions');
      return res.json();
    },
    enabled: isAdmin
  });
  
  // Fetch subscription plans
  const {
    data: plans = [],
    isLoading: isLoadingPlans,
  } = useQuery<AdminPlan[]>({
    queryKey: ['/api/subscription-plans/all'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/subscription-plans/all');
      return res.json();
    },
    enabled: isAdmin
  });

  // Form for creating new customer
  const form = useForm<CreateCustomerFormData>({
    resolver: zodResolver(createCustomerSchema),
    defaultValues: {
      email: "",
      fullName: "",
      businessName: "",
      industry: "",
      planId: "",
    },
  });

  // Handle create customer
  const createCustomerMutation = useMutation({
    mutationFn: async (data: CreateCustomerFormData) => {
      const res = await apiRequest("POST", "/api/admin/customers", data);
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Customer created successfully",
        description: `New customer account created. Temporary password: ${data.tempPassword}`,
      });
      setIsCreateDialogOpen(false);
      form.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/subscriptions'] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create customer",
        description: error.message || "There was an error creating the customer account",
        variant: "destructive",
      });
    },
  });

  // Industry options
  const industryOptions = [
    { value: "technology", label: "Technology" },
    { value: "healthcare", label: "Healthcare" },
    { value: "finance", label: "Finance" },
    { value: "retail", label: "Retail" },
    { value: "manufacturing", label: "Manufacturing" },
    { value: "consulting", label: "Consulting" },
    { value: "real_estate", label: "Real Estate" },
    { value: "construction", label: "Construction" },
    { value: "education", label: "Education" },
    { value: "hospitality", label: "Hospitality" },
    { value: "other", label: "Other" },
  ];
  
  // Filter users based on search query
  const filteredUsers = users.filter((user: any) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      user.username?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.fullName?.toLowerCase().includes(searchLower)
    );
  });
  
  // Handle client selection for detailed view
  const handleUserSelect = (user: any) => {
    setSelectedUser(user);
    setSelectedBusiness(null);
    setSelectedSubscription(null);
  };
  
  // Handle business selection for subscription management
  const handleBusinessSelect = (business: any) => {
    setSelectedBusiness(business);
    setSelectedSubscription(business.subscription || null);
  };
  
  // Mutation to upgrade a subscription plan
  const upgradePlanMutation = useMutation({
    mutationFn: async ({ subscriptionId, newPlanId }: { subscriptionId: number; newPlanId: number }) => {
      const res = await apiRequest('POST', `/api/admin/subscriptions/${subscriptionId}/upgrade`, {
        newPlanId
      });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Subscription upgraded successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/subscriptions'] });
      setShowUpgradeDialog(false);
      setNewPlanId(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to upgrade subscription: ${error.message}`,
        variant: "destructive"
      });
    }
  });
  
  // Check if the user is admin, else show access denied
  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="container max-w-screen-xl py-12">
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
            <p className="text-muted-foreground">
              You don't have permission to access this page. This page is restricted to admin users only.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Debug: User email: {user?.email}, isAdmin check result: {isAdmin ? 'true' : 'false'}
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  // Format currency amount
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Get CSS class for subscription status
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Handle upgrade plan dialog submission
  const handleUpgradeSubmit = () => {
    if (!selectedSubscription || !newPlanId) return;
    
    upgradePlanMutation.mutate({
      subscriptionId: selectedSubscription.id,
      newPlanId
    });
  };
  
  return (
    <AdminLayout>
      <div className="container max-w-screen-xl py-6">
        <div className="space-y-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Client Management</h1>
              <p className="text-muted-foreground">
                Manage client accounts, subscriptions, and billing information.
              </p>
            </div>
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="users" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Clients
              </TabsTrigger>
              <TabsTrigger value="subscriptions" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Subscriptions
              </TabsTrigger>
            </TabsList>
            
            {/* Users Tab */}
            <TabsContent value="users" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle>All Clients</CardTitle>
                  <CardDescription>
                    View and manage all registered clients
                  </CardDescription>
                  <div className="mt-4 flex items-center justify-between gap-4">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search clients..."
                        className="pl-8"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    
                    {/* Add New Customer Button */}
                    <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="flex items-center gap-2">
                          <Plus className="h-4 w-4" />
                          Add New Customer
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[600px]">
                        <DialogHeader>
                          <DialogTitle>Create New Customer Account</DialogTitle>
                          <DialogDescription>
                            Set up a new customer with their business and subscription plan.
                          </DialogDescription>
                        </DialogHeader>
                        
                        <Form {...form}>
                          <form onSubmit={form.handleSubmit((data) => createCustomerMutation.mutate(data))} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={form.control}
                                name="fullName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Full Name</FormLabel>
                                    <FormControl>
                                      <Input placeholder="John Doe" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              
                              <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Email Address</FormLabel>
                                    <FormControl>
                                      <Input placeholder="john@example.com" type="email" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                              <FormField
                                control={form.control}
                                name="businessName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Business Name</FormLabel>
                                    <FormControl>
                                      <Input placeholder="Acme Corporation" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              
                              <FormField
                                control={form.control}
                                name="industry"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Industry</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                      <FormControl>
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select industry" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        {industryOptions.map((option) => (
                                          <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            
                            <FormField
                              control={form.control}
                              name="planId"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Subscription Plan</FormLabel>
                                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select subscription plan" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {plans.map((plan: any) => (
                                        <SelectItem key={plan.id} value={plan.id.toString()}>
                                          {plan.name} - ${plan.price}/month
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            
                            <DialogFooter>
                              <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                                Cancel
                              </Button>
                              <Button type="submit" disabled={createCustomerMutation.isPending}>
                                {createCustomerMutation.isPending ? "Creating..." : "Create Customer"}
                              </Button>
                            </DialogFooter>
                          </form>
                        </Form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Username</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Full Name</TableHead>
                        <TableHead>Businesses</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoadingUsers ? (
                        Array.from({ length: 5 }).map((_, index) => (
                          <TableRow key={index}>
                            <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-8" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                            <TableCell><Skeleton className="h-8 w-20" /></TableCell>
                          </TableRow>
                        ))
                      ) : filteredUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                            No clients found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredUsers.map((user: any) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              <div className="font-medium">{user.username}</div>
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.fullName || "N/A"}</TableCell>
                            <TableCell>
                              <Badge>{user.businesses?.length || 0}</Badge>
                            </TableCell>
                            <TableCell>{formatDate(user.createdAt)}</TableCell>
                            <TableCell>
                              <Button variant="outline" onClick={() => handleUserSelect(user)}>
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              
              {/* Client Details Card */}
              {selectedUser && (
                <Card>
                  <CardHeader>
                    <CardTitle>Client Details: {selectedUser.username}</CardTitle>
                    <CardDescription>
                      Client account information and businesses
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-2">Account Information</h3>
                        <dl className="space-y-2">
                          <div className="flex justify-between">
                            <dt className="font-medium">Username:</dt>
                            <dd>{selectedUser.username}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="font-medium">Email:</dt>
                            <dd>{selectedUser.email}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="font-medium">Full Name:</dt>
                            <dd>{selectedUser.fullName || "N/A"}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="font-medium">Joined:</dt>
                            <dd>{formatDate(selectedUser.createdAt)}</dd>
                          </div>
                        </dl>
                      </div>
                      
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-2">Email Preferences</h3>
                        <dl className="space-y-2">
                          <div className="flex justify-between">
                            <dt className="font-medium">Reports Summary:</dt>
                            <dd>{selectedUser.emailNotifications?.reportsSummary ? "Enabled" : "Disabled"}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="font-medium">Task Reminders:</dt>
                            <dd>{selectedUser.emailNotifications?.taskReminders ? "Enabled" : "Disabled"}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="font-medium">Subscription Alerts:</dt>
                            <dd>{selectedUser.emailNotifications?.subscriptionAlerts ? "Enabled" : "Disabled"}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="font-medium">Marketing Emails:</dt>
                            <dd>{selectedUser.emailNotifications?.marketingEmails ? "Enabled" : "Disabled"}</dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                    
                    <div className="mt-6">
                      <h3 className="text-md font-semibold mb-2">Businesses</h3>
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead>Industry</TableHead>
                              <TableHead>Subscription</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedUser.businesses?.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center py-4 text-muted-foreground">
                                  No businesses found
                                </TableCell>
                              </TableRow>
                            ) : (
                              selectedUser.businesses?.map((business: any) => (
                                <TableRow key={business.id}>
                                  <TableCell>
                                    <div className="font-medium">{business.name}</div>
                                  </TableCell>
                                  <TableCell>{business.industry || "N/A"}</TableCell>
                                  <TableCell>
                                    {business.plan ? business.plan.name : "No subscription"}
                                  </TableCell>
                                  <TableCell>
                                    {business.subscription ? (
                                      <Badge 
                                        variant={business.subscription.status === "active" ? "default" : 
                                                business.subscription.status === "canceled" ? "destructive" : "outline"}
                                      >
                                        {business.subscription.status}
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline">No subscription</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Button variant="outline" onClick={() => handleBusinessSelect(business)}>
                                      Manage Subscription
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Business Subscription Management */}
              {selectedBusiness && (
                <Card>
                  <CardHeader>
                    <CardTitle>Subscription Management: {selectedBusiness.name}</CardTitle>
                    <CardDescription>
                      Manage subscription details and billing information
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedBusiness.subscription ? (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h3 className="text-sm font-medium text-muted-foreground mb-2">Current Subscription</h3>
                            <dl className="space-y-2">
                              <div className="flex justify-between">
                                <dt className="font-medium">Plan:</dt>
                                <dd>{selectedBusiness.plan?.name || "N/A"}</dd>
                              </div>
                              <div className="flex justify-between">
                                <dt className="font-medium">Price:</dt>
                                <dd>{formatCurrency(selectedBusiness.plan?.price || 0)}/month</dd>
                              </div>
                              <div className="flex justify-between">
                                <dt className="font-medium">Status:</dt>
                                <dd>
                                  <Badge 
                                    variant={selectedBusiness.subscription.status === "active" ? "default" : 
                                            selectedBusiness.subscription.status === "canceled" ? "destructive" : "outline"}
                                  >
                                    {selectedBusiness.subscription.status}
                                  </Badge>
                                </dd>
                              </div>
                              <div className="flex justify-between">
                                <dt className="font-medium">Start Date:</dt>
                                <dd>{formatDate(selectedBusiness.subscription.startDate)}</dd>
                              </div>
                              {selectedBusiness.subscription.endDate && (
                                <div className="flex justify-between">
                                  <dt className="font-medium">End Date:</dt>
                                  <dd>{formatDate(selectedBusiness.subscription.endDate)}</dd>
                                </div>
                              )}
                              {selectedBusiness.subscription.stripeSubscriptionId && (
                                <div className="flex justify-between">
                                  <dt className="font-medium">Stripe ID:</dt>
                                  <dd className="font-mono text-xs">{selectedBusiness.subscription.stripeSubscriptionId}</dd>
                                </div>
                              )}
                            </dl>
                          </div>
                          
                          <div>
                            <h3 className="text-sm font-medium text-muted-foreground mb-2">Plan Features</h3>
                            <ul className="space-y-1">
                              {selectedBusiness.plan?.features?.map((feature: string, index: number) => (
                                <li key={index} className="flex items-start">
                                  <Check className="h-4 w-4 mr-2 text-green-500 mt-0.5" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        
                        <div className="mt-4 flex flex-col md:flex-row gap-4">
                          <Button 
                            onClick={() => setShowUpgradeDialog(true)}
                            className="w-full md:w-auto"
                          >
                            <CreditCard className="h-4 w-4 mr-2" />
                            Upgrade Plan
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="py-6 text-center">
                        <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <h3 className="text-lg font-medium mb-1">No Active Subscription</h3>
                        <p className="text-muted-foreground mb-4">
                          This business doesn't have an active subscription.
                        </p>
                        <Button 
                          onClick={() => setShowUpgradeDialog(true)}
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Add Subscription
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            {/* Subscriptions Tab */}
            <TabsContent value="subscriptions" className="space-y-4">
              <div className="text-center py-8">
                <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Customer Subscriptions</h3>
                <p className="text-muted-foreground mb-4">
                  Customer subscription management has been moved to a dedicated page with detailed views for active, expired, and cancelled subscriptions.
                </p>
                <Button onClick={() => window.location.href = '/dashboard/customer-subscriptions'}>
                  Go to Customer Subscriptions
                </Button>
              </div>
              <Card>
                <CardHeader>
                  <CardTitle>All Subscriptions</CardTitle>
                  <CardDescription>
                    View and manage all business subscriptions
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Business</TableHead>
                        <TableHead>Owner</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Start Date</TableHead>
                        <TableHead>End Date</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoadingSubscriptions ? (
                        Array.from({ length: 5 }).map((_, index) => (
                          <TableRow key={index}>
                            <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                            <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                            <TableCell><Skeleton className="h-8 w-20" /></TableCell>
                          </TableRow>
                        ))
                      ) : subscriptions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-6 text-muted-foreground">
                            No subscriptions found
                          </TableCell>
                        </TableRow>
                      ) : (
                        subscriptions.map((subscription: any) => (
                          <TableRow key={subscription.id}>
                            <TableCell>
                              <div className="font-medium">{subscription.business?.name || "N/A"}</div>
                            </TableCell>
                            <TableCell>{subscription.user?.username || "N/A"}</TableCell>
                            <TableCell>{subscription.plan?.name || "N/A"}</TableCell>
                            <TableCell>
                              <Badge 
                                variant={subscription.status === "active" ? "default" : 
                                        subscription.status === "canceled" ? "destructive" : "outline"}
                              >
                                {subscription.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatDate(subscription.startDate)}</TableCell>
                            <TableCell>{subscription.endDate ? formatDate(subscription.endDate) : "N/A"}</TableCell>
                            <TableCell>{formatCurrency(subscription.plan?.price || 0)}</TableCell>
                            <TableCell>
                              <Button 
                                variant="outline" 
                                onClick={() => {
                                  setSelectedSubscription(subscription);
                                  setShowUpgradeDialog(true);
                                }}
                                disabled={subscription.status !== "active"}
                              >
                                Upgrade
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      {/* Upgrade Plan Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upgrade Subscription Plan</DialogTitle>
            <DialogDescription>
              Select a new plan for this business.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {isLoadingPlans ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Select Plan</label>
                    <Select
                      value={newPlanId?.toString() || ""}
                      onValueChange={(value) => setNewPlanId(parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {plans
                          .filter((plan: any) => plan.isActive)
                          .filter((plan: any) => {
                            // Only show plans with higher price for upgrades
                            if (!selectedSubscription || !selectedSubscription.plan) return true;
                            return plan.price > (selectedSubscription.plan?.price || 0);
                          })
                          .map((plan: any) => (
                            <SelectItem key={plan.id} value={plan.id.toString()}>
                              {plan.name} - {formatCurrency(plan.price)}/month
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {newPlanId && (
                    <div className="rounded-md border p-4 bg-muted/50">
                      <h3 className="font-medium mb-2">
                        {plans.find((p: any) => p.id === newPlanId)?.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-2">
                        {formatCurrency(plans.find((p: any) => p.id === newPlanId)?.price || 0)}/month
                      </p>
                      <ul className="text-sm space-y-1">
                        {plans.find((p: any) => p.id === newPlanId)?.features?.map((feature: string, i: number) => (
                          <li key={i} className="flex items-start">
                            <Check className="h-3 w-3 mr-2 text-green-500 mt-1" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <DialogFooter className="flex space-x-2 justify-end">
            <Button variant="outline" onClick={() => setShowUpgradeDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpgradeSubmit}
              disabled={!newPlanId || upgradePlanMutation.isPending}
            >
              {upgradePlanMutation.isPending ? "Upgrading..." : "Upgrade Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
